import { MenuItem } from '@cloudclevr/components';
import {
  useAddDashboardTile,
  useDashboardUpdateTileParams,
  useHandleTileBtnNavigate,
  useRemoveDashboardTile,
} from '@hooks/dashboard';
import { useLicencesUsageTrendQuery } from '@hooks/licenses';
import { useState } from 'react';

import {
  pickSummary,
  useDualAdoptionData,
  useExtendedTimeSeriesQuery,
  useSummaryApisEnabled,
} from '@hooks/summary';
import { GroupedSummaryDto } from '@hooks/summary/types';
import { toServiceUsageTrend } from '@hooks/summary/adapters';
import { ANALYTICS_NAVIGATION_MAP, DashboardTiles } from '@common/constants';
import { TilesProps } from '@common/types';
import {
  LineChartTile,
  LineChartTypes,
} from '@pages/InsightsAndAnalytics/tiles/LineChartTile';
import { getTrendData, getTrendLabels } from '@utils/index';

type Params = {
  days: string;
  serviceType: string;
};

export const AdoptionServiceUsageTrendOverTime: React.FC<TilesProps> = ({
  params,
  id,
  classNames,
}) => {
  const [parameters, setParameters] = useState<Params>(JSON.parse(params));
  const summaryEnabled = useSummaryApisEnabled();
  const extendedDays = parameters.days === '60' || parameters.days === '90'
    ? (Number(parameters.days) as 60 | 90)
    : undefined;

  const { data: extendedTimeSeries } = useExtendedTimeSeriesQuery(
    'adoption',
    extendedDays ?? 60,
    summaryEnabled && !!extendedDays,
  );

  const getEffectiveUserActivitySummary = (grouped: GroupedSummaryDto | undefined) => {
    const userActivitySummary = pickSummary(grouped, 'UserActivity');
    if (!userActivitySummary || !extendedDays || !extendedTimeSeries?.summaries) return userActivitySummary;

    const resourceTypeKey = Object.keys(extendedTimeSeries.summaries).find(
      (key) => key.toLowerCase() === userActivitySummary.resourceType.toLowerCase(),
    );

    if (!resourceTypeKey) return userActivitySummary;

    return {
      ...userActivitySummary,
      timeSeries: {
        ...userActivitySummary.timeSeries,
        ...extendedTimeSeries.summaries[resourceTypeKey],
      },
    };
  };

  const useDashboardUpdateTileParamsMutation = useDashboardUpdateTileParams();
  const { handleButtonClick } = useHandleTileBtnNavigate(id);
  const { removeMenuItem } = useRemoveDashboardTile(id);
  const { addMenuItem } = useAddDashboardTile(
    DashboardTiles.ServiceUsageTrendOverTime,
    parameters
  );
  const isOnDashboardPage = !!id;

  const {
    data: licencesUsageTrendData,
    isLoading: isLicencesUsageTrendDataLoading,
    refetch,
  } = useDualAdoptionData({
    legacyParams: parameters,
    useLegacyQuery: useLicencesUsageTrendQuery,
    mapSummary: (grouped) => {
      const effectiveUserActivitySummary = getEffectiveUserActivitySummary(grouped);
      return toServiceUsageTrend(effectiveUserActivitySummary, parameters.serviceType, parameters.days);
    },
  });

  const labels = getTrendLabels(
    licencesUsageTrendData?.trend || [],
    +parameters.days
  );

  const trendData = getTrendData(
    licencesUsageTrendData?.trend || [],
    +parameters.days
  );

  const handleDropdown = (value: string) => {
    const newParameters = {
      ...parameters,
      serviceType: value,
    };

    setParameters(newParameters);

    if (isOnDashboardPage) {
      useDashboardUpdateTileParamsMutation.mutate({
        params: JSON.stringify(newParameters),
        tileId: id,
      });
    }

    refetch();
  };

  const handleCompareDropdown = (value: string) => {
    const newParameters = {
      ...parameters,
      days: value,
    };

    setParameters(newParameters);

    if (isOnDashboardPage) {
      useDashboardUpdateTileParamsMutation.mutate({
        params: JSON.stringify(newParameters),
        tileId: id,
      });

      refetch();
    }
  };

  const sortMenuItems: MenuItem[] = [
    {
      id: '1',
      label: 'Messaging',
      value: 'messaging',
      action: () => handleDropdown('messaging'),
    },
    {
      id: '2',
      label: 'Email',
      value: 'email',
      action: () => handleDropdown('email'),
    },
    {
      id: '3',
      label: 'Video calls',
      value: 'videocalls',
      action: () => handleDropdown('videocalls'),
    },
    {
      id: '4',
      label: 'Phone calls',
      value: 'phonecalls',
      action: () => handleDropdown('phonecalls'),
    },
    {
      id: '5',
      label: 'File storage',
      value: 'filestorage',
      action: () => handleDropdown('filestorage'),
    },
  ];

  const compareMenuItems: MenuItem[] = [
    {
      id: '1',
      label: 'Last 7 days',
      value: '7',
      action: () => handleCompareDropdown('7'),
    },
    {
      id: '2',
      label: 'Last 30 days',
      value: '30',
      action: () => handleCompareDropdown('30'),
    },
    {
      id: '3',
      label: 'Last 60 days',
      value: '60',
      action: () => handleCompareDropdown('60'),
    },
    {
      id: '4',
      label: 'Last 90 days',
      value: '90',
      action: () => handleCompareDropdown('90'),
    },
  ];

  return (
    parameters && (
      <LineChartTile
        classNames={
          classNames ? classNames : 'col-span-6 md:col-span-6 xl:col-span-4'
        }
        sortData={sortMenuItems}
        compareData={compareMenuItems}
        legendLabels={['Microsoft']}
        contextData={isOnDashboardPage ? removeMenuItem : addMenuItem}
        initialSelectedItem={sortMenuItems.find(
          (item) => item.value === parameters.serviceType
        )}
        initialCompareSelectedItem={compareMenuItems.find(
          (item) => item.value === parameters.days
        )}
        tooltipLabel={['Microsoft']}
        headerTitle={'Service usage: Trend over time'}
        isLoading={isLicencesUsageTrendDataLoading}
        labels={labels}
        data={[trendData]}
        compareLabel=""
        dropdownMenuItemClasses={'!w-[300px]'}
        buttonLabel="Service usage data"
        dataSetTypes={[LineChartTypes.Dots]}
        dataSetColors={['#00CF6C']}
        linkPath={ANALYTICS_NAVIGATION_MAP['serviceUsage']}
        handleButtonClick={handleButtonClick()}
        showXTicks={!isOnDashboardPage}
        showDateRange={true}
      />
    )
  );
};
